import { useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  FolderOpen,
  Package,
  ShoppingCart,
  Calendar,
  UserCog,
  Warehouse,
  PackageCheck,
  Shield,
  FileText,
  Wrench,
  Bell,
  AlarmClock,
  CalendarHeart,
  Palette,
  ClipboardPen,
  Wallet,
} from 'lucide-react';
import { NavigationDrawer } from '@elka/ui';
import type { NavItemConfig, UserAction } from '@elka/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useIsAdmin, useIsSuperAdmin } from '@/features/auth/hooks/useIsAdmin';
import { useTheme } from '@elka/theme';
import { SectionSwitcher } from './SectionSwitcher';
import { useOpenPurchaseRequestCount } from '@/features/purchase-requests/hooks/usePurchaseRequests';
import { usePendingPettyCashCount } from '@/features/admin/hooks/useAdminPettyCash';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const getNavItems = (
  section: 'projects' | 'rechesh' | 'admin',
  isSuperAdmin: boolean,
  openPurchaseRequests: number,
  pendingPettyCash: number
): NavItemConfig[] => {
  if (section === 'admin') {
    return [
      { to: '/admin/vacation-requests', icon: Calendar, label: 'חופשה' },
      {
        to: '/admin/petty-cash',
        icon: Wallet,
        label: pendingPettyCash > 0 ? `קופה קטנה (${pendingPettyCash})` : 'קופה קטנה',
      },
      ...(isSuperAdmin ? [{ to: '/admin/payslips', icon: FileText, label: 'תלושים' }] : []),
      { to: '/admin/time-reports', icon: Package, label: 'שעות' },
      { to: '/admin/notifications', icon: Bell, label: 'הודעות' },
      { to: '/admin/reminder-settings', icon: AlarmClock, label: 'תזכורות' },
      { to: '/admin/holidays', icon: CalendarHeart, label: 'חגים' },
      { to: '/admin/workers', icon: UserCog, label: 'עובדים' },
      ...(isSuperAdmin ? [{ to: '/admin/audit-logs', icon: Shield, label: 'ביקורת' }] : []),
    ];
  }

  if (section === 'projects') {
    return [
      { to: '/projects/clients', icon: Users, label: 'לקוחות' },
      { to: '/projects/list', icon: FolderOpen, label: 'פרויקטים' },
      { to: '/projects/sidur', icon: Calendar, label: 'סידור עבודה' },
      { to: '/projects/daily-reports', icon: Package, label: 'יומני עבודה' },
      { to: '/projects/toolbox', icon: Wrench, label: 'ארגז כלים' },
    ];
  }

  return [
    { to: '/rechesh/orders', icon: ShoppingCart, label: 'הזמנות רכש' },
    { to: '/rechesh/supplier-bids', icon: FileText, label: 'הצעות מחיר רכש' },
    { to: '/rechesh/suppliers', icon: Package, label: 'ספקים' },
    { to: '/rechesh/supplies', icon: Warehouse, label: 'מלאי' },
    { to: '/rechesh/goods-receiving', icon: PackageCheck, label: 'קבלת סחורה' },
    {
      to: '/rechesh/purchase-requests',
      icon: ClipboardPen,
      // NavItemConfig has no badge slot — show the open count in the label.
      label: openPurchaseRequests > 0 ? `בקשות רכש (${openPurchaseRequests})` : 'בקשות רכש',
    },
  ];
};

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { user, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const currentSection = location.pathname.startsWith('/admin')
    ? 'admin'
    : location.pathname.startsWith('/rechesh')
      ? 'rechesh'
      : 'projects';

  const openPurchaseRequests = useOpenPurchaseRequestCount();
  const pendingPettyCash = usePendingPettyCashCount();
  const navItems = getNavItems(
    currentSection,
    isSuperAdmin,
    openPurchaseRequests,
    pendingPettyCash
  );

  const displayName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || '';
  const userInitial = displayName.charAt(0).toUpperCase();

  const userActions: UserAction[] = [];
  if (isAdmin) {
    userActions.push({
      icon: Shield,
      label: 'ניהול',
      onClick: () => navigate('/admin/vacation-requests'),
      className:
        currentSection === 'admin'
          ? 'bg-indigo-100 text-indigo-700'
          : 'text-indigo-600 hover:bg-indigo-50',
    });
  }

  const handleSignOut = async () => {
    try {
      // useAuth().signOut also clears the session tenant + hard-reloads to /login,
      // so mobile logout doesn't leak the previous tenant into a fresh session.
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const themeToggle = (
    <button
      onClick={toggleTheme}
      className="rounded-control border-line bg-surface text-ink-2 hover:bg-surface-soft hover:border-line-strong flex w-full items-center justify-center gap-2 border px-4 py-2.5 text-sm font-semibold transition-[background-color,border-color,transform] active:scale-[0.96]"
    >
      <Palette size={16} className="text-muted" />
      <span>{theme === 'elkaBlue' ? 'ערכת זהב' : 'ערכת כחול'}</span>
    </button>
  );

  return (
    <NavigationDrawer
      isOpen={isOpen}
      onClose={onClose}
      headerContent={<SectionSwitcher hideAdmin />}
      items={navItems}
      footerContent={themeToggle}
      user={user ? { name: displayName, initial: userInitial, photoURL: user.photoURL } : undefined}
      userActions={userActions.length > 0 ? userActions : undefined}
      onSignOut={handleSignOut}
    />
  );
}
